import {
  AddressCard,
  BackPage,
  BasketPageBar,
  DeliveryOptionCard,
  OrderBar,
  PaymentOptionCard,
  Spinner,
  WarningContainer,
} from 'components';
import { useOrders, useP5Orders, useIsOffboardingPharmacy } from 'hooks';
import alertTriangleIcon from 'assets/alert-triangle.svg';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { BasketPageContent, DeliveryOptionPageContent } from 'services/content';
import styles from './styles.module.css';
import { allMedicationBasketIsContraceptive} from 'helpers/helperFunctions';

const Basket = () => {
  const history = useHistory();
  const [notesCount, setNotesCount] = useState(0);
  const [GPNotes, setGPNotes] = useState('');
  const [postOrder] = useOrders();
  const [postP5Order] = useP5Orders();
  const [placingOrder, setPlacingOrder] = useState(false);

  const {
    paymentJourney,
    medication: medicationState,
    patient: { isP9 },
  } = useSelector((state) => state);

  const [offboardingData, loading, getIsOffboarding] = useIsOffboardingPharmacy();

  useEffect(() => {
    !paymentJourney.loading && getIsOffboarding(paymentJourney.address.ods_code);
  }, [paymentJourney])

  useEffect(() => {
    if (!paymentJourney.started) history.push('/pharmacy/dashboard');
  }, [paymentJourney.started, history]);

  const basketMedication = medicationState.medication.filter((medication) => {
    if (isP9) {
      return paymentJourney.basket.includes(medication.course_id);
    } else {
      return paymentJourney.basket.includes(medication.id);
    }
  });

  const totalCost =
    Object.keys(paymentJourney.exemption).length !== 0
      ? '0.00'
      : isP9
      ? basketMedication.reduce((accum, medic) => accum + medic.price.amount, 0).toFixed(2)
      : (basketMedication.reduce((accum, medic) => accum + medic.price_in_pence, 0) / 100).toFixed(2);

  const updateNotes = (newNotes) => {
    setNotesCount(newNotes.target.value.length);
    setGPNotes(newNotes.target.value);
  };

  const placeOrder = async () => {
    try {
      setPlacingOrder(true);
      const orderResponse = isP9
        ? await postOrder(paymentJourney, medicationState, basketMedication, GPNotes, totalCost)
        : await postP5Order(paymentJourney, medicationState, basketMedication, GPNotes, totalCost);
      setPlacingOrder(false);
      if (orderResponse?.data?.redirect_url) {
        window.location.href = orderResponse?.data?.redirect_url;
      } else {
        history.push('/pharmacy/dashboard/confirmation');
      }
    } catch (e) {
      setPlacingOrder(false);
      history.push('/pharmacy/error');
    }
  };

  const displayMedication = () => {
    if (basketMedication.length === 0) return <div>Empty Basket</div>;
    return (
      <ul className={`${styles.medicationContainer} drop-shadow`}>
        {basketMedication.map((medication) => (
          <li className={styles.medicationIndividualContainer} key={isP9 ? medication.course_id : medication.id}>
            <h3 className={styles.medicationTitle}>{medication.name}</h3>
            {paymentJourney.delivery_type === 'homeDelivery' && (
              <p className={styles.medicationPrice}>
                {(paymentJourney.exemption.type && Object.keys(paymentJourney.payment).length === 0) || medication.is_contraceptive
                  ? 'Free'
                  : isP9
                  ? '£' + medication.price.amount.toFixed(2)
                  : '£' + (medication.price_in_pence / 100).toFixed(2)}
              </p>
            )}
          </li>
        ))}
      </ul>
    );
  };

  if (placingOrder || loading) {
    return (
      <>
        <OrderBar currentProgress={1} />
        <div className={styles.loadingScreen}>
          <Spinner />
        </div>
      </>
    );
  }

  return (
    <>
      <OrderBar currentProgress={1} />
      <BackPage onBackClick={() => history.goBack()} bplg="10">
        <div className={styles.grid}>
          <div>
            <h1 className={styles.header}>{BasketPageContent.headingText}</h1>
            {displayMedication()}
            {((medicationState.gp_settings && medicationState.gp_settings.prescription_comment_allowed) || !isP9) && (
              <>
                <h3 className={`${styles.sectionTitle} ${styles.sectionTitleNoTop}`}>Notes for your GP</h3>
                <textarea
                  className={`${styles.notesInput} drop-shadow`}
                  onChange={(e) => updateNotes(e)}
                  name="Text1"
                  maxLength="256"></textarea>
                <p className={styles.count}>{notesCount}/256</p>
              </>
            )}
          </div>

          <div className={styles.sections}>
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>Delivery method</h3>
                <button
                  className={styles.link_button}
                  href="#"
                  onClick={() => history.push('/pharmacy/dashboard/delivery-options')}>
                  Change
                </button>
              </div>
              {paymentJourney.delivery_type && (
                <DeliveryOptionCard data={DeliveryOptionPageContent[paymentJourney.delivery_type]} />
              )}
            </div>
            {offboardingData?.showBasketMessage && offboardingData?.offboardingDate ? (
              <>
                <WarningContainer
                  type="warning"
                  icon={alertTriangleIcon}
                  heading={BasketPageContent.offBoardingText.heading}
                  text={BasketPageContent.offBoardingText.text.replace('{date}', offboardingData?.offboardingDate)}>
                  <div className={styles.warningContainerCTA}>
                    <a
                      href="https://intercom-help.eu/heypharmacist/en/articles/27976-my-local-pharmacy-isn-t-showing"
                      target="_blank"
                      rel="noreferrer">
                      Learn More
                    </a>
                  </div>
                </WarningContainer>
              </>
            ) : (
              <></>
            )}
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>
                  {paymentJourney.delivery_type === 'homeDelivery' ||
                  paymentJourney.delivery_type === 'pharmacyDelivery'
                    ? 'Delivery address'
                    : 'Collection address'}
                </h3>
                <button
                  className={styles.link_button}
                  href="#"
                  onClick={
                    paymentJourney.delivery_type === 'homeDelivery' ||
                    paymentJourney.delivery_type === 'pharmacyDelivery'
                      ? () => history.push('/pharmacy/dashboard/delivery-details')
                      : () => history.push('/pharmacy/dashboard/find-pharmacy')
                  }>
                  Change
                </button>
              </div>
              {paymentJourney.address && paymentJourney.delivery_type && (
                <AddressCard address={paymentJourney.address} />
              )}
            </div>
            {!allMedicationBasketIsContraceptive(basketMedication) && paymentJourney.exemption.type && Object.keys(paymentJourney.payment).length === 0 && (
              <div className={styles.section} data-testid="exemption-container">
                <div className={styles.sectionHeader}>
                  <h3 className={styles.sectionTitle}>Exemption</h3>
                  <button
                    className={styles.link_button}
                    href="#"
                    onClick={() => history.push('/pharmacy/dashboard/exemption')}>
                    Change
                  </button>
                </div>
                <div className={`${styles.exemption} drop-shadow`}>{paymentJourney.exemption.type.text}</div>
              </div>
            )}
            {!allMedicationBasketIsContraceptive(basketMedication) && Object.keys(paymentJourney.payment).length !== 0 && (
              <div className={styles.section} data-testid="payment-container">
                <div className={styles.sectionHeader}>
                  <h3 className={styles.sectionTitle}>Payment</h3>
                  <button
                    className={styles.link_button}
                    href="#"
                    onClick={() => history.push('/pharmacy/dashboard/exemption')}>
                    Change
                  </button>
                </div>

                <PaymentOptionCard paymentOption={paymentJourney.payment} />
              </div>
            )}
          </div>
        </div>
      </BackPage>
      <BasketPageBar
        isPharmacyCollection={
          paymentJourney.delivery_type === 'collectFromPharmacy' || paymentJourney.delivery_type === 'pharmacyDelivery'
        }
        price={totalCost}
        callBack={() => placeOrder()}
      />
    </>
  );
};

export default Basket;
